import { useState, useEffect, useRef } from "react";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { X, Upload, FileSpreadsheet, Check, AlertCircle, Trash2 } from "lucide-react";

export default function ImportVesselsModal({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [importedFiles, setImportedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const fileInputRef = useRef(null);
  const [mappingFileId, setMappingFileId] = useState(null);
  const [mappingHeaders, setMappingHeaders] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [mapping, setMapping] = useState({
    email_col: "",
    name_col: "",
    company_col: "",
    contact_person_col: "",
    port_col: "",
    imo_col: ""
  });

  useEffect(() => {
    fetchImportedFiles();
  }, []);

  const fetchImportedFiles = async () => {
    try {
      const response = await api.get("/vessels/imports");
      setImportedFiles(response.data);
      setSelectedFileIds(prev => prev.filter(id => response.data.some(f => f.file_id === id)));
    } catch (error) {
      console.error("Failed to load imported files");
    }
  };

  const handleFileChange = (e) => {
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = [];
    const invalidFiles = [];

    for (const file of files) {
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!validExtensions.includes(ext)) {
        invalidFiles.push(file.name);
        continue;
      }
      validFiles.push(file);
    }

    if (invalidFiles.length > 0) {
      toast.error(`Skipped invalid files: ${invalidFiles.join(", ")}`);
    }

    if (validFiles.length === 0) {
      return;
    }

    setSelectedFiles(validFiles);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setLoading(true);
    const results = [];
    const failures = [];
    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append("file", file);
        try {
          const response = await api.post("/vessels/import", formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          results.push(response.data);
        } catch (error) {
          failures.push({
            name: file.name,
            error: error.response?.data?.detail || "Failed to upload file"
          });
        }
      }

      if (results.length > 0) {
        const totalEmails = results.reduce((acc, r) => acc + (r.total_emails || 0), 0);
        setUploadResult({
          count: results.length,
          total_emails: totalEmails,
          files: results
        });
        if (results.length === 1) {
          setMappingFileId(results[0].file_id);
          setMappingHeaders([]);
          setPreviewRows([]);
          setShowPreview(false);
          setMapping({
            email_col: "",
            name_col: "",
            company_col: "",
            contact_person_col: "",
            port_col: "",
            imo_col: ""
          });
          fetchHeaders(results[0].file_id);
        }
        toast.success(`Uploaded ${results.length} file(s): ${totalEmails} emails found`);
      }

      if (failures.length > 0) {
        toast.error(`Failed ${failures.length} file(s). Check the list below.`);
      }

      fetchImportedFiles();
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchHeaders = async (fileId) => {
    try {
      const response = await api.get(`/vessels/imports/${fileId}/preview`);
      setMappingHeaders(response.data.headers || []);
      setPreviewRows([]);
      setShowPreview(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load preview");
    }
  };

  const fetchPreview = async (fileId) => {
    try {
      const response = await api.get(`/vessels/imports/${fileId}/preview`);
      setMappingHeaders(response.data.headers || []);
      setPreviewRows(response.data.rows || []);
      setShowPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load preview");
    }
  };

  const applyMapping = async () => {
    if (!mappingFileId) return;
    const hasAny = Object.values(mapping).some((v) => v);
    if (!hasAny) {
      toast.info("No mapping selected. Using auto-detected columns.");
      return;
    }
    if (!mapping.email_col) {
      toast.error("Email column is required");
      return;
    }
    try {
      const response = await api.post(`/vessels/imports/${mappingFileId}/map`, mapping);
      toast.success(`Mapping applied. ${response.data.total_emails} emails ready`);
      fetchImportedFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply mapping");
    }
  };

  const handleRemoveSelected = (fileName) => {
    setSelectedFiles(prev => prev.filter(file => file.name !== fileName));
  };

  const handleConfirmImport = async (fileId) => {
    setLoading(true);
    try {
      const response = await api.post(`/vessels/imports/${fileId}/confirm`);
      toast.success(`Imported ${response.data.imported} vessels (${response.data.skipped} skipped)`);
      fetchImportedFiles();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import vessels");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;
    
    try {
      await api.delete(`/vessels/imports/${fileId}`);
      toast.success("File deleted");
      fetchImportedFiles();
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFileIds(prev => 
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    );
  };

  const handleSelectAllFiles = () => {
    setSelectedFileIds(importedFiles.map(f => f.file_id));
  };

  const handleClearSelection = () => {
    setSelectedFileIds([]);
  };

  const handleDeleteSelected = async () => {
    if (selectedFileIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedFileIds.length} selected files?`)) return;
    
    try {
      const response = await api.post("/vessels/imports/bulk-delete", { file_ids: selectedFileIds });
      toast.success(`Deleted ${response.data.deleted} files`);
      setSelectedFileIds([]);
      fetchImportedFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete files");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="badge-scheduled">Pending</Badge>;
      case "imported":
        return <Badge className="badge-sent">Imported</Badge>;
      case "failed":
        return <Badge className="badge-cancelled">Failed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fadeIn">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Import Vessels
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Upload Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Upload File
            </h3>
            
            <div 
              className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-2">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-muted-foreground">
                CSV or Excel files containing vessel emails
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                multiple
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-input"
              />
            </div>

            {selectedFiles.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">{selectedFiles.length} file(s) selected</p>
                      <p className="text-xs text-muted-foreground">
                        {(
                          selectedFiles.reduce((acc, f) => acc + f.size, 0) / 1024
                        ).toFixed(1)} KB total
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleUpload} 
                    disabled={loading}
                    className="btn-primary"
                    data-testid="upload-btn"
                  >
                    {loading ? "Uploading..." : "Upload All"}
                  </Button>
                </div>

                <div className="space-y-2">
                  {selectedFiles.map((file) => (
                    <div key={file.name} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveSelected(file.name)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploadResult && (
              <div className="p-4 bg-chart-2/10 border border-chart-2/30 rounded-lg">
                <div className="flex items-center gap-2 text-chart-2 mb-2">
                  <Check className="h-5 w-5" />
                  <span className="font-medium">Upload Complete</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Uploaded <span className="font-mono font-bold text-foreground">{uploadResult.count}</span> file(s) with{" "}
                  <span className="font-mono font-bold text-foreground">{uploadResult.total_emails}</span> emails total
                </p>
              </div>
            )}

            {mappingFileId && (
              <div className="p-4 bg-secondary/30 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">Optional: Map Columns</p>
                    <p className="text-xs text-muted-foreground">Select columns to override auto-detection</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => fetchPreview(mappingFileId)}>
                      Preview 20 Rows
                    </Button>
                    <Button variant="outline" size="sm" onClick={applyMapping}>
                      Apply Mapping
                    </Button>
                  </div>
                </div>

                {mappingHeaders.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { key: "email_col", label: "Email (required)" },
                      { key: "name_col", label: "Name" },
                      { key: "company_col", label: "Company" },
                      { key: "contact_person_col", label: "Contact Person" },
                      { key: "port_col", label: "Port" },
                      { key: "imo_col", label: "IMO" }
                    ].map((field) => (
                      <div key={field.key} className="space-y-1">
                        <Label className="text-xs">{field.label}</Label>
                        <select
                          value={mapping[field.key]}
                          onChange={(e) => setMapping(prev => ({ ...prev, [field.key]: e.target.value }))}
                          className="w-full px-3 py-2 bg-secondary border border-border rounded-md text-foreground text-sm"
                        >
                          <option value="">Auto</option>
                          {mappingHeaders.map((h) => (
                            <option key={`${field.key}-${h}`} value={h}>{h}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}

                {showPreview && previewRows.length > 0 && (
                  <div className="border border-border rounded-lg overflow-auto max-h-64">
                    <table className="w-full text-xs">
                      <thead className="bg-secondary/50">
                        <tr>
                          {mappingHeaders.map((h) => (
                            <th key={h} className="px-3 py-2 text-left text-muted-foreground">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {previewRows.map((row, idx) => (
                          <tr key={idx} className="hover:bg-secondary/30">
                            {mappingHeaders.map((h) => (
                              <td key={`${idx}-${h}`} className="px-3 py-2 text-muted-foreground">
                                {String(row[h] ?? "")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Imported Files List - CRITICAL: Shows ONE entry per file, NOT individual emails */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Imported Files
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={selectedFileIds.length === importedFiles.length ? handleClearSelection : handleSelectAllFiles}
                  disabled={importedFiles.length === 0}
                >
                  {selectedFileIds.length === importedFiles.length ? "Clear Selection" : "Select All"}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={selectedFileIds.length === 0}
                >
                  Delete Selected
                </Button>
              </div>
            </div>
            
            {importedFiles.length === 0 ? (
              <div className="text-center py-8">
                <FileSpreadsheet className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No imported files yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {importedFiles.map((file) => (
                  <div 
                    key={file.file_id}
                    className="flex items-center justify-between p-4 bg-secondary/30 border border-border rounded-lg"
                    data-testid={`imported-file-${file.file_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedFileIds.includes(file.file_id)}
                        onChange={() => toggleFileSelection(file.file_id)}
                        className="h-4 w-4"
                      />
                      <FileSpreadsheet className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium">{file.file_name}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="font-mono">{file.total_emails} emails</span>
                          <span>•</span>
                          <span>{new Date(file.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {getStatusBadge(file.status)}
                      
                      {file.status === "pending" && (
                        <Button
                          size="sm"
                          onClick={() => handleConfirmImport(file.file_id)}
                          disabled={loading}
                          className="btn-primary"
                          data-testid={`confirm-import-${file.file_id}`}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Confirm Import
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteFile(file.file_id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="p-4 bg-secondary/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-chart-3 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">File Format Requirements:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>CSV or Excel format (.csv, .xlsx, .xls)</li>
                  <li>Must contain an "email" column or column with @ symbols</li>
                  <li>One email per row</li>
                  <li>Duplicate emails will be automatically skipped</li>
                  <li>Click "Confirm Import" to add vessels and unlock the file for campaigns</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
